import React from "react"
import { useMemo, useCallback, type Ref } from "react"
import { useDispatch } from "react-redux"
import OpLayoutEngineRenderer from "connected-components/op-layout-engine/renderer"
import { TEMPLATES } from "connected-components/op-layout-engine/templates"
import type { OpLayoutEngineRendererActions } from "connected-components/op-layout-engine/types"
import type { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { Creators as DialogCreators } from "modules/dialog/model"
import { useModules } from "../context/modules"
import { useDashboardOpLayoutEnginePersistence } from "../hooks/use-op-layout-engine-persistence"
import { createModuleComponents } from "../helpers/module-renderers"
import { getModuleDefinition } from "../modules/registry"
import { IDashboardContextValue } from "../context/types"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"

interface DashboardModulesRendererProps {
  TradeContext: React.Context<IDashboardContextValue>
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  hasMultipleInstruments?: boolean
  contentSets?: unknown[]
  modelMetadata?: unknown
  dockviewLayoutRef?: Ref<OpLayoutEngineRendererActions | null>
  onLayoutReady?: (actions: OpLayoutEngineRendererActions | null) => void
}

/**
 * Renders dashboard modules using OpLayoutEngine
 */
export default function DashboardModulesRenderer({
  TradeContext,
  instrumentsWithMetadata,
  hasMultipleInstruments,
  contentSets,
  modelMetadata,
  dockviewLayoutRef,
  onLayoutReady,
}: DashboardModulesRendererProps) {
  const dispatch = useDispatch()
  const { modules, isPreferencesLoaded: isModulesPreferencesLoaded, removeModule, updateModule } = useModules()
  const opLayoutEnginePersistence = useDashboardOpLayoutEnginePersistence(modules, isModulesPreferencesLoaded)

  const handleRemoveModule = useCallback((moduleId: string) => {
    const module = modules.find((m) => m.id === moduleId)

    if (!module) return

    const definition = getModuleDefinition(module.type)
    if (!definition.removable) return

    const performRemoval = () => {
      dashboardEvents.emit(DASHBOARD_EVENTS.MODULE_REMOVED, module)
      removeModule(moduleId)
    }

    dispatch(DialogCreators.open({
      title: "Remove module?",
      contentText: "This cannot be undone. You will need to repin from the AI teammate if you want to add it again.",
      buttons: [
        {
          text: "Delete",
          type: "danger",
          action: performRemoval,
        },
      ],
    }))
  }, [dispatch, modules, removeModule])

  const handleUpdateModuleTitle = useCallback((moduleId: string, newTitle: string) => {
    updateModule(moduleId, {
      config: {
        title: newTitle,
      },
    })
  }, [updateModule])

  const components = useMemo(() => {
    return createModuleComponents(modules, {
      TradeContext,
      instrumentsWithMetadata,
      hasMultipleInstruments,
      contentSets,
      modelMetadata,
      onRemoveModule: handleRemoveModule,
      onUpdateModuleTitle: handleUpdateModuleTitle,
    })
  }, [modules, TradeContext, contentSets, hasMultipleInstruments, instrumentsWithMetadata, modelMetadata, handleRemoveModule, handleUpdateModuleTitle])

  return (
    <OpLayoutEngineRenderer
      layoutRef={dockviewLayoutRef ?? null}
      onLayoutReady={onLayoutReady}
      components={components}
      template={TEMPLATES.HIERARCHICAL}
      persistence={opLayoutEnginePersistence}
      className="dashboard-op-layout"
    />
  )
}
